// A detail screen's work-files card: WorkFilesBlock plus the fetch it needs when it
// stands on its own. The exact counterpart of AgreementsSection, one table over.
//
// `ownerKind` picks WHAT is asked for, and the two questions genuinely differ:
//
//   job       one job's uploads — the files a writer filed against this piece of work
//   retainer  every upload across the retainer's TOPICS, because a retainer holds no
//             work files of its own. A writer granted a retainer uploads to the topics
//             beneath it, so the retainer screen showing nothing would be the most
//             misleading answer of all — it is exactly where a manager looks first.
//
// The retainer case is assembled from the client-wide read filtered to this retainer's
// topics, rather than from a new endpoint: the job ids are already on screen.
import useAsync from '../hooks/useAsync';
import he from '../i18n/he';
import { jobsApi } from '../api';
import AsyncBoundary from './Loader';
import WorkFilesBlock from './WorkFilesBlock';

export default function WorkFilesSection({ ownerKind = 'job', ownerId, jobIds }) {
  const isRetainer = ownerKind === 'retainer';
  const { data, loading, error, reload } = useAsync(
    () => (isRetainer
      // Every work file on any of this retainer's topics. `jobIds` comes from the topics
      // already rendered above it, so no second read of the retainer's shape is needed.
      ? Promise.all((jobIds || []).map((id) => jobsApi.workFiles(id)))
        .then((lists) => lists.flat())
      : jobsApi.workFiles(ownerId)),
    [ownerKind, ownerId, (jobIds || []).join(',')],
  );

  const files = data || [];

  // Quiet when there is nothing, on the RETAINER only. A job screen says "no files"
  // because that is a fact about this job worth stating; a retainer with no uploads
  // anywhere beneath it would just be an empty card on every retainer in the system.
  if (isRetainer && !loading && files.length === 0) return null;

  return (
    <div className="card work-files-card">
      <AsyncBoundary loading={loading} error={error}>
        <WorkFilesBlock
          files={files}
          onChange={reload}
          heading={isRetainer ? he.workFiles.retainerTitle : he.workFiles.title}
          // On a retainer the rows come from several topics, so each says which.
          showJob={isRetainer}
        />
      </AsyncBoundary>
    </div>
  );
}
